import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Célula multi-select inline para lookups fechados (ex: setores).
 *
 * Props:
 * - value: array de códigos atual (ex: ['desenvolvedor', 'proprietario'])
 * - onSave(newArray): callback ao fechar o popover se houve mudança; recebe array ou null (quando vazio)
 * - options: [{ value, label }]
 * - placeholder: texto quando vazio
 * - className
 */
export default function MultiSelectCell({ value, onSave, options = [], placeholder = '—', className = '' }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(Array.isArray(value) ? value : []);
  const ref = useRef(null);

  useEffect(() => {
    setLocal(Array.isArray(value) ? value : []);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        const prev = Array.isArray(value) ? value : [];
        const changed = local.length !== prev.length || local.some((v, i) => v !== prev[i]);
        if (changed) onSave(local.length ? local : null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, local, value, onSave]);

  const toggle = (code) => {
    setLocal((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  };

  const labelMap = new Map(options.map((o) => [o.value, o.label]));
  const displayText = Array.isArray(value) && value.length > 0
    ? value.map((c) => labelMap.get(c) || c).join(', ')
    : placeholder;

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-left hover:bg-slate-100 rounded px-1 py-0.5 w-full truncate flex items-center gap-1"
        title={displayText}
      >
        <span className="truncate flex-1">{displayText}</span>
        <ChevronDown size={10} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 min-w-[200px] bg-white border border-slate-200 rounded shadow-lg py-1 max-h-64 overflow-y-auto">
          {options.length === 0 && (
            <div className="px-2 py-1 text-[11px] text-slate-400 italic">Sem opções</div>
          )}
          {options.map((opt) => {
            const checked = local.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggle(opt.value)}
                className={`w-full px-2 py-1 text-left text-xs flex items-center gap-2 ${
                  checked ? 'bg-blue-50 text-blue-900' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className={`w-3 h-3 rounded border flex items-center justify-center ${
                  checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                }`}>
                  {checked && <Check size={9} className="text-white" />}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
